// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.campaign.spec.ts
import { ArrowUp, CircleNotch } from "@phosphor-icons/react";
import { Button, Flex, Heading, Text, TextArea } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { buildProjectChatPath } from "./askPunttPaths";
import { askPunttChatRoute } from "./chatRoute";
import { AskPunttDropZone } from "./components/AskPunttDropZone";
import { CreateAskPunttProjectDialog } from "./components/CreateAskPunttProjectDialog";
import {
  HomeComposerModeToggle,
  type HomeComposerMode,
} from "./components/HomeComposerModeToggle";
import { HomeProjectPicker } from "./components/HomeProjectPicker";
import { HomeTaskList } from "./components/HomeTaskList";
import { InputPlusMenu } from "./components/InputPlusMenu";
import { type ConnectorOption } from "./components/InputPlusMenu";
import { PasswordProtectedPdfDialog } from "./components/PasswordProtectedPdfDialog";
import { TryBadge } from "./components/try-walkthrough/TryBadge";
import {
  getTryFeatureForCard,
  HOME_PROJECT_TRY_FEATURE,
} from "./components/try-walkthrough/tryFeatures";
import {
  TryWalkthroughDialog,
  type TryLaunchRequest,
} from "./components/try-walkthrough/TryWalkthroughDialog";
import { SkillCards } from "./components/WorkflowCards";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useFileDrop } from "./hooks/useFileDrop";
import { useUiPrefs } from "./hooks/useUiPrefs";
import { WORKFLOW_CARDS } from "./mockData";
import { resolveNewProjectWorkflowType } from "./projectWorkflowType";

import { getGuidelines } from "../../services/brand-guidelines";
import { PasswordProtectedPdfError } from "../../services/upload";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { canUseAskPunttConnectors } from "../../utils/auth";
import { useCreateAskPunttChat } from "../../utils/queries/askPuntt";
import { useParticipationTasks } from "../../utils/queries/askPunttProjects";

/** Matches the centered composer column — workflows share this width. */
const ASK_PUNTT_HOME_CONTENT_CLASS = "mx-auto w-full max-w-[900px] px-4";

/**
 * Visible height the absolute project lane adds below the composer after the
 * `-mt-4` tuck (pt-5 + h-8 + pb-1 − mt-4 ≈ 40px). Kept in normal flow *outside*
 * the composer’s relative wrapper so it does not inflate `top-full`.
 */
const HOME_PROJECT_LANE_SPACER_CLASS = "h-10";

const PROJECT_LANE_EXIT_MS = 220;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function AskPunttHome() {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const { t: tComponents } = useTranslation("components");
  const [inputMessage, setInputMessage] = useState("");
  const [composerMode, setComposerMode] = useState<HomeComposerMode>("chat");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(
    null,
  );
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [projectLaneMounted, setProjectLaneMounted] = useState(false);
  const [projectLaneExiting, setProjectLaneExiting] = useState(false);
  const projectLaneExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const analytics = useAnalytics("ask_puntt_home");
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles({
    onUploadError: (err) => {
      if (err instanceof PasswordProtectedPdfError) {
        setProtectedPdfFileName(err.fileName);
      }
      analytics.capture("ask_puntt_upload_error", {
        ...errorAnalyticsPayload(err),
      });
    },
  });
  const [protectedPdfFileName, setProtectedPdfFileName] = useState<
    string | null
  >(null);
  // Page-wide drag-and-drop is owned by the outer AskPunttDropZone wrapper
  // below. Only retain the clipboard paste handler here so users can still
  // paste screenshots / images directly into the composer.
  const { handlePaste } = useFileDrop({ onDrop: addFiles });
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const enabledSkillIds = guidelinesQuery.data?.askPunttSkills
    ?.filter((s) => s.enabled)
    .map((s) => s.skillId);
  const customWorkflows = guidelinesQuery.data?.customWorkflows;

  const hasWorkflows =
    (enabledSkillIds
      ? WORKFLOW_CARDS.some((c) => enabledSkillIds.includes(c.skillId))
      : WORKFLOW_CARDS.length > 0) ||
    (customWorkflows ?? []).some((wf) => wf.enabled);

  useEffect(() => {
    if (composerMode === "project") {
      if (projectLaneExitTimerRef.current != null) {
        clearTimeout(projectLaneExitTimerRef.current);
        projectLaneExitTimerRef.current = null;
      }
      setProjectLaneMounted(true);
      setProjectLaneExiting(false);
      return;
    }
    if (!projectLaneMounted) return;
    if (prefersReducedMotion()) {
      setProjectLaneMounted(false);
      setProjectLaneExiting(false);
      return;
    }
    setProjectLaneExiting(true);
    projectLaneExitTimerRef.current = setTimeout(() => {
      setProjectLaneMounted(false);
      setProjectLaneExiting(false);
      projectLaneExitTimerRef.current = null;
    }, PROJECT_LANE_EXIT_MS);
    return () => {
      if (projectLaneExitTimerRef.current != null) {
        clearTimeout(projectLaneExitTimerRef.current);
        projectLaneExitTimerRef.current = null;
      }
    };
  }, [composerMode, projectLaneMounted]);

  const openTasksQuery = useParticipationTasks({ status: "open" });
  const hasActiveTasks = (openTasksQuery.data?.tasks?.length ?? 0) > 0;

  // Derive available connectors from enterprise settings — show all enabled,
  // mark unconnected ones as disabled (greyed out in UI). Role-gated: OPS
  // doesn't see connectors (`canUseAskPunttConnectors()` returns false),
  // matching `ask.puntt.connectors.spec.ts > Not visible for OPS`.
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      canUseAskPunttConnectors()
        ? (guidelinesQuery.data?.askPunttConnectors ?? [])
            .filter((c) => c.enabled)
            .map((c) => ({
              id: c.connectorId,
              label: t("home.connectorSearchLabel", { name: c.name }),
              icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
              disabled: !c.connected,
            }))
        : [],
    [guidelinesQuery.data?.askPunttConnectors, t],
  );

  const [enabledConnectors, setEnabledConnectors] = useState<
    Record<string, boolean>
  >({});

  // Try walkthrough one-shot flags (localStorage). Deep Artwork Review is
  // launched with reviewMode "deep"; typed/composer submits stay "standard".
  const { prefs, setPref } = useUiPrefs();
  const [tryWalkthroughCardId, setTryWalkthroughCardId] = useState<
    string | null
  >(null);
  const lastTryFeatureRef =
    useRef<ReturnType<typeof getTryFeatureForCard>>(undefined);

  // Initialize connector state when enterprise data loads
  useEffect(() => {
    if (availableConnectors.length > 0) {
      setEnabledConnectors((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        const initial: Record<string, boolean> = {};
        availableConnectors.forEach((c) => {
          initial[c.id] = !c.disabled;
        });
        return initial;
      });
    }
  }, [availableConnectors]);

  useEffect(() => {
    analytics.capture("ask_puntt_home_viewed");

    // Auto-focus the textarea when the page loads
    const timer = setTimeout(() => {
      // Radix UI's TextArea renders as a textarea element itself, not a wrapper
      const textarea = document.querySelector(
        '[data-testid="ask-puntt-home-input"]',
      ) as HTMLTextAreaElement | null;
      if (textarea) {
        textarea.focus();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const markTryFeatureSeen = (featureId: string) => {
    setPref("seenTryFeatures", {
      ...(prefs.seenTryFeatures ?? {}),
      [featureId]: true,
    });
  };

  const handleSubmit = async (
    message?: string,
    workflowType?: string,
    opts?: {
      workflowGreeting?: boolean;
      reviewMode?: "standard" | "deep";
      tryFeatureId?: string;
      composerMode?: HomeComposerMode;
      projectId?: string | null;
    },
  ): Promise<boolean> => {
    const msg = message || inputMessage;
    const attachments = [...readyAttachments];
    if (!msg.trim() && attachments.length === 0) return false;
    if (hasPending) return false;

    // Composer submit (no skill workflow) cannot run in Project mode without a
    // selected project. Skill cards attach to that project when one is selected.
    // Try compose/skip can override mode + project so a walkthrough submit is
    // not tied to the home picker state behind the modal.
    const submitMode = opts?.composerMode ?? composerMode;
    const submitProjectId =
      opts?.projectId !== undefined ? opts.projectId : selectedProjectId;
    const isComposerSubmit =
      workflowType === undefined && !opts?.workflowGreeting;
    const isProjectSubmit = submitMode === "project" && submitProjectId != null;
    if (isComposerSubmit && submitMode === "project" && !submitProjectId) {
      return false;
    }

    const wfType = workflowType || "general";
    const enabledConnectorIds = Object.entries(enabledConnectors)
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    // Only request the canned greeting when nothing was attached — with an
    // attachment the user wants the real review to run (the backend enforces
    // the same guard).
    const workflowGreeting =
      opts?.workflowGreeting === true && attachments.length === 0;

    // Deep Artwork Review card (and its Try walkthrough) forces "deep"; typed
    // / composer submits without that card default to "standard" (v1).
    const reviewMode = opts?.reviewMode ?? "standard";

    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: wfType,
        attachments: attachments.length > 0 ? attachments : undefined,
        enabledConnectors: enabledConnectorIds,
        workflowGreeting: workflowGreeting || undefined,
        reviewMode,
        ...(isProjectSubmit && submitProjectId != null
          ? {
              projectId: submitProjectId,
              projectChatKind: "personal" as const,
            }
          : {}),
      });
      const attachmentIds = attachments.map((att) => att.key);

      analytics.capture("ask_puntt_chat_created", {
        workflow_type: wfType,
        message_length: msg.length,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: enabledConnectorIds,
        workflow_greeting: workflowGreeting,
        ...(isProjectSubmit
          ? {
              project_id: submitProjectId,
              project_chat_kind: "personal",
            }
          : {}),
        review_mode: reviewMode,
      });
      if (opts?.tryFeatureId) {
        markTryFeatureSeen(opts.tryFeatureId);
      }
      clearAll();
      if (isProjectSubmit && submitProjectId != null) {
        navigate(buildProjectChatPath(submitProjectId, result.chat._id));
      } else {
        navigate({
          to: askPunttChatRoute.to,
          params: { chatId: result.chat._id },
        });
      }
      return true;
    } catch (error) {
      const attachmentIds = attachments.map((att) => att.key);

      analytics.capture("ask_puntt_chat_create_error", {
        workflow_type: wfType,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: enabledConnectorIds,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to create chat:", error);
      return false;
    }
  };

  const launchWorkflow = async (cardId: string) => {
    // Project mode without a selection must not silently create a standalone chat.
    if (composerMode === "project" && !selectedProjectId) {
      return;
    }
    // Don't consume Try if we cannot submit yet — marking seen before
    // createChat succeeds would hide the badge with no chat behind it.
    if (hasPending || createChat.isPending) return;

    const cardMessages: Record<string, string> = {
      marketing_review: t("workflowGreeting.marketing_review"),
      artwork_review: t("workflowGreeting.artwork_review"),
      claims_intelligence: t("workflowGreeting.claims_intelligence"),
      shared_projects: t("workflowGreeting.shared_projects"),
      connectors: t("workflowGreeting.connectors"),
    };
    const card = WORKFLOW_CARDS.find((c) => c.id === cardId);

    // The Shared Projects card resolves its workflowType via
    // resolveNewProjectWorkflowType (Vitalora demo → "general", so the
    // project_management overlay doesn't fight the live-AI baseline-capture
    // overlay — see projectWorkflowType.ts). Other cards open their declared
    // workflowType.
    const workflowType =
      cardId === "shared_projects"
        ? resolveNewProjectWorkflowType()
        : (card?.workflowType ?? cardId);

    // Only home page workflow cards open with the canned greeting.
    const workflowGreeting = card != null;

    let label = cardMessages[cardId];
    if (!label && cardId.startsWith("custom_")) {
      const wfId = cardId.replace("custom_", "");
      const wf = customWorkflows?.find((w) => w.workflowId === wfId);
      label = wf
        ? t("workflowGreeting.customWorkflow", { name: wf.name })
        : t("workflowGreeting.hello");
    }

    const tryFeature = getTryFeatureForCard(cardId);

    analytics.capture("ask_puntt_workflow_card_clicked", {
      workflow_type: workflowType,
      card_id: cardId,
      card_title: label ?? cardId,
    });
    const launched = await handleSubmit(label || "Hello", workflowType, {
      workflowGreeting,
      reviewMode: tryFeature?.launchReviewMode,
      tryFeatureId: tryFeature?.featureId,
    });
    if (launched) {
      setTryWalkthroughCardId(null);
    }
  };

  /** While a Try badge is still showing, the card opens the walkthrough;
   * after Skip / Get started (seenTryFeatures), it launches the skill. */
  const handleWorkflowClick = (cardId: string) => {
    const tryFeature = getTryFeatureForCard(cardId);
    if (
      tryFeature != null &&
      prefs.seenTryFeatures?.[tryFeature.featureId] !== true
    ) {
      setTryWalkthroughCardId(cardId);
      return;
    }
    void launchWorkflow(cardId);
  };

  const tryFeatureOpen =
    tryWalkthroughCardId != null
      ? getTryFeatureForCard(tryWalkthroughCardId)
      : undefined;
  // Keep the last feature so Dialog.Root stays mounted while `open` goes
  // false. Unmounting an open Radix dialog skips RemoveScroll cleanup and
  // leaves `body { pointer-events: none }`, so the Deep Review card cannot
  // create a chat after Skip / Get started.
  if (tryFeatureOpen != null) {
    lastTryFeatureRef.current = tryFeatureOpen;
  }
  const dialogFeature = tryFeatureOpen ?? lastTryFeatureRef.current;

  const handleTryLaunch = (request?: TryLaunchRequest) => {
    if (tryWalkthroughCardId === HOME_PROJECT_TRY_FEATURE.cardId) {
      void launchHomeProjectTry(request);
      return;
    }
    if (tryWalkthroughCardId != null) {
      void launchWorkflow(tryWalkthroughCardId);
    }
  };

  const launchHomeProjectTry = async (request?: TryLaunchRequest) => {
    if (hasPending || createChat.isPending) return;
    const compose =
      request?.source === "getStarted" ? request.compose : undefined;
    const mode = compose?.mode ?? "chat";
    const message =
      compose?.message?.trim() || t("tryWalkthrough.home_project.skipMessage");
    if (mode === "project" && request?.source === "getStarted") {
      if (compose?.projectId == null) return;
    }
    const launched = await handleSubmit(message, undefined, {
      tryFeatureId: HOME_PROJECT_TRY_FEATURE.featureId,
      reviewMode: HOME_PROJECT_TRY_FEATURE.launchReviewMode,
      composerMode: mode,
      projectId: mode === "project" ? (compose?.projectId ?? null) : null,
    });
    if (launched) {
      setTryWalkthroughCardId(null);
    }
  };

  const showHomeProjectTry =
    prefs.seenTryFeatures?.[HOME_PROJECT_TRY_FEATURE.featureId] !== true;

  const needsProjectSelection =
    composerMode === "project" && selectedProjectId == null;
  const submitDisabled =
    (!inputMessage.trim() && readyAttachments.length === 0) ||
    createChat.isPending ||
    hasPending ||
    needsProjectSelection;
  const submitAriaLabel = needsProjectSelection
    ? t("home.submitNeedsProjectAriaLabel")
    : tCommon("nav.askPuntt");

  return (
    <AskPunttDropZone
      onFilesDropped={addFiles}
      className="ask-puntt-mesh-bg ask-puntt-noise relative flex h-full flex-col"
      data-testid="ask-puntt-home"
    >
      {/* Centered heading + input */}
      <div
        className={`${ASK_PUNTT_HOME_CONTENT_CLASS} flex flex-1 flex-col`}
        data-testid="ask-puntt-home-main-column"
      >
        {hasActiveTasks ? (
          <div className="shrink-0 pt-16" aria-hidden="true" />
        ) : (
          <div
            className="min-h-0 flex-1"
            aria-hidden="true"
            data-testid="ask-puntt-home-top-flex-spacer"
          />
        )}

        <div className="stagger-children mb-8 flex flex-col items-center gap-3">
          <div className="animate-glow-ring relative z-10 flex size-16 items-center justify-center rounded-2xl bg-puntt-accent-3 shadow-lg">
            <svg
              width="36"
              height="35"
              viewBox="0 0 30 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.4939 5.67486H15.8328V2.34435C15.8328 2.12352 15.7451 1.91174 15.589 1.75559C15.4328 1.59944 15.221 1.51172 15.0002 1.51172C14.7794 1.51172 14.5676 1.59944 14.4114 1.75559C14.2553 1.91174 14.1676 2.12352 14.1676 2.34435V5.67486H7.50654C6.62323 5.67486 5.7761 6.02576 5.15151 6.65035C4.52692 7.27494 4.17603 8.12207 4.17603 9.00538V20.6622C4.17603 21.5455 4.52692 22.3926 5.15151 23.0172C5.7761 23.6418 6.62323 23.9927 7.50654 23.9927H22.4939C23.3772 23.9927 24.2243 23.6418 24.8489 23.0172C25.4735 22.3926 25.8244 21.5455 25.8244 20.6622V9.00538C25.8244 8.12207 25.4735 7.27494 24.8489 6.65035C24.2243 6.02576 23.3772 5.67486 22.4939 5.67486Z"
                fill="rgb(var(--puntt-blue-9))"
              />
              <circle
                cx="10.428"
                cy="13.5947"
                r="2.91667"
                stroke="white"
                strokeWidth="1.66667"
              />
              <circle
                cx="19.5947"
                cy="13.5947"
                r="2.91667"
                stroke="white"
                strokeWidth="1.66667"
              />
            </svg>
          </div>
          <Text
            size="8"
            weight="bold"
            className="text-center text-puntt-neutral-gray-12"
          >
            {t("home.ask")}{" "}
            <span
              style={{
                backgroundImage:
                  "linear-gradient(135deg, rgb(var(--puntt-blue-9)), rgb(var(--puntt-blue-11)))",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              {tComponents("askPuntt.feed.puntt")}
            </span>
          </Text>
        </div>

        {/* Relative only around composer + absolute lane. The flow spacer MUST
            stay outside this box — otherwise `top-full` includes spacer height
            and the lane drops ~40px below the card. */}
        <div className="relative z-10">
          <div
            className="relative z-20 overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-lg transition-all duration-200 focus-within:border-puntt-accent-7"
            data-testid="ask-puntt-home-composer"
          >
            <TextArea
              data-testid="ask-puntt-home-input"
              size="3"
              variant="surface"
              placeholder={
                composerMode === "project"
                  ? t("home.askInProject")
                  : t("askAnything")
              }
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!submitDisabled) {
                    handleSubmit();
                  }
                }
              }}
              onPaste={handlePaste}
              className="w-full resize-none"
              style={{ height: "88px" }}
            />

            {/* Attached files preview */}
            {attachedItems.length > 0 && (
              <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                {attachedItems.map((item, idx) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                    data-testid="ask-puntt-attached-file"
                  >
                    {item.status === "pending" && (
                      <CircleNotch
                        size={12}
                        weight="bold"
                        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                      />
                    )}
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "error" && " (failed)"}
                    </Text>
                    <button
                      data-testid="ask-puntt-home-remove-attachment"
                      onClick={() => removeFile(idx)}
                      className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                      aria-label={t("home.removeFileAriaLabel", {
                        name: item.file.name,
                      })}
                    >
                      ×
                    </button>
                  </Flex>
                ))}
              </Flex>
            )}

            <Flex justify="between" align="center" className="p-1">
              <Flex align="center" gap="2">
                <InputPlusMenu
                  iconSize={16}
                  onFilesSelected={addFiles}
                  onScreenshotCaptured={addFile}
                  enabledConnectors={enabledConnectors}
                  onConnectorToggle={(id, enabled) => {
                    setEnabledConnectors((prev) => ({
                      ...prev,
                      [id]: enabled,
                    }));
                  }}
                  availableConnectors={availableConnectors}
                />
                <HomeComposerModeToggle
                  data-testid="ask-puntt-home-composer-mode"
                  value={composerMode}
                  onChange={setComposerMode}
                />
                {showHomeProjectTry ? (
                  <TryBadge
                    data-testid={`ask-puntt-try-badge-${HOME_PROJECT_TRY_FEATURE.featureId}`}
                    featureId={HOME_PROJECT_TRY_FEATURE.featureId}
                    onClick={() =>
                      setTryWalkthroughCardId(HOME_PROJECT_TRY_FEATURE.cardId)
                    }
                  />
                ) : null}
              </Flex>

              <Flex align="center" gap="2">
                <Button
                  data-testid="ask-puntt-home-submit"
                  size="2"
                  className="cursor-pointer rounded-xl"
                  disabled={submitDisabled}
                  aria-label={submitAriaLabel}
                  title={needsProjectSelection ? submitAriaLabel : undefined}
                  onClick={() => handleSubmit()}
                >
                  {hasPending ? (
                    <CircleNotch
                      size={14}
                      weight="bold"
                      className="animate-spin"
                    />
                  ) : (
                    <ArrowUp size={14} weight="bold" />
                  )}
                  {tCommon("nav.askPuntt")}
                </Button>
              </Flex>
            </Flex>
          </div>

          {/* Detached from the composer card so toggling Project does not
              grow the card or re-center/shift the textarea. Absolute under the
              card (`top-full` of this relative wrapper, not including spacer). */}
          {projectLaneMounted && (
            <div
              className={
                projectLaneExiting
                  ? "animate-ask-puntt-home-project-lane-out absolute inset-x-0 top-full z-10 origin-top"
                  : "animate-ask-puntt-home-project-lane-in absolute inset-x-0 top-full z-10 origin-top"
              }
              data-testid="ask-puntt-home-project-lane"
              data-exiting={projectLaneExiting ? "true" : "false"}
              onAnimationEnd={() => {
                if (!projectLaneExiting) return;
                if (projectLaneExitTimerRef.current != null) {
                  clearTimeout(projectLaneExitTimerRef.current);
                  projectLaneExitTimerRef.current = null;
                }
                setProjectLaneMounted(false);
                setProjectLaneExiting(false);
              }}
            >
              <HomeProjectPicker
                selectedProjectId={selectedProjectId}
                selectedProjectName={selectedProjectName}
                onSelect={(project) => {
                  setSelectedProjectId(project.id);
                  setSelectedProjectName(project.name);
                }}
                onClear={() => {
                  setSelectedProjectId(null);
                  setSelectedProjectName(null);
                }}
                onNewProject={() => setCreateProjectOpen(true)}
              />
            </div>
          )}
        </div>

        {/* Flow reserve lives below the composer anchor so opening the project
            lane pushes tasks/workflows down without re-centering the hero card. */}
        <div
          className={
            hasActiveTasks ? "shrink-0" : "flex min-h-0 flex-1 flex-col"
          }
          data-testid="ask-puntt-home-below-composer"
        >
          {projectLaneMounted && (
            <div
              aria-hidden="true"
              data-testid="ask-puntt-home-project-lane-spacer"
              className={HOME_PROJECT_LANE_SPACER_CLASS}
            />
          )}

          <HomeTaskList />
        </div>
      </div>

      {/* Workflows anchored to bottom */}
      <div
        className={`${ASK_PUNTT_HOME_CONTENT_CLASS} relative z-10 shrink-0 pb-6`}
        data-testid="ask-puntt-home-bottom"
      >
        {hasWorkflows && (
          <>
            <Flex justify="between" align="center" className="mb-3">
              <Heading
                as="h2"
                size="4"
                weight="bold"
                className="text-puntt-neutral-gray-12"
              >
                {t("home.workflowsHeading")}
              </Heading>
            </Flex>
            <SkillCards
              onCardClick={handleWorkflowClick}
              seenTryFeatures={prefs.seenTryFeatures}
              enabledSkillIds={enabledSkillIds}
              customWorkflows={customWorkflows}
            />
          </>
        )}
      </div>
      {dialogFeature != null && (
        <TryWalkthroughDialog
          open={tryWalkthroughCardId != null}
          feature={dialogFeature}
          onLaunch={handleTryLaunch}
          onDismiss={() => setTryWalkthroughCardId(null)}
          onFilesSelected={addFiles}
          attachedItems={attachedItems}
          onRemoveFile={removeFile}
          hasPendingUploads={hasPending}
          hasReadyAttachments={readyAttachments.length > 0}
          isLaunching={createChat.isPending}
        />
      )}
      <PasswordProtectedPdfDialog
        fileName={protectedPdfFileName}
        onClose={() => setProtectedPdfFileName(null)}
      />
      <CreateAskPunttProjectDialog
        open={createProjectOpen}
        onOpenChange={setCreateProjectOpen}
        onCreated={(project) => {
          setSelectedProjectId(project.id);
          setSelectedProjectName(project.name);
        }}
      />
    </AskPunttDropZone>
  );
}
